import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Order } from '@models/order';
import { formatNumber } from '@angular/common';

export interface ReturnOrderDialogData {
  message: string;
  order: Order;
  /** When true, only show credit (e.g. void). Hides fee / inventory options. */
  creditOnly?: boolean;
}

export interface ReturnOrderDialogResult {
  credit: number;
  restockingFee: number;
  netCredit: number;
  returnToInventory: boolean;
}

@Component({
  selector: 'app-return-order-dialog',
  templateUrl: './return-order-dialog.component.html',
  styleUrls: ['./return-order-dialog.component.css'],
})
export class ReturnOrderDialogComponent {
  credit: any = 0;
  restockingFee: any = '0.00';
  returnToInventory = true;
  creditOnly = false;

  constructor(
    public dialogRef: MatDialogRef<ReturnOrderDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ReturnOrderDialogData
  ) {
    this.creditOnly = !!data?.creditOnly;
    const order: Order = data.order;
    const retail = order?.products?.[0]?.retail;
    this.credit =
      retail === undefined || retail === null
        ? '0.00'
        : this.formatNumberValue(retail);
  }

  get netCredit(): number {
    return Math.max(0, this.parseCurrency(this.credit) - this.parseCurrency(this.restockingFee));
  }

  get netCreditDisplay(): string {
    return formatNumber(this.netCredit, 'en-US', '1.2-2');
  }

  onNoClick(): void {
    this.dialogRef.close(false);
  }

  cancel(): void {
    this.dialogRef.close(false);
  }

  return(): void {
    this.dialogRef.close(this.buildResult());
  }

  /** Kept for callers that still read componentInstance after close. */
  buildResult(): ReturnOrderDialogResult {
    const credit = this.parseCurrency(this.credit);
    const restockingFee = this.creditOnly ? 0 : this.parseCurrency(this.restockingFee);
    const netCredit = Math.max(0, credit - restockingFee);
    return {
      credit,
      restockingFee,
      netCredit,
      returnToInventory: this.creditOnly ? false : this.returnToInventory,
    };
  }

  formatCurrency(field: 'credit' | 'restockingFee' = 'credit'): void {
    if (field === 'restockingFee') {
      this.restockingFee = this.formatNumberValue(this.restockingFee);
      return;
    }
    this.credit = this.formatNumberValue(this.credit);
  }

  private formatNumberValue(value: any): string {
    return formatNumber(this.parseCurrency(value), 'en-US', '1.2-2');
  }

  private parseCurrency(value: any): number {
    if (typeof value === 'number') {
      return isNaN(value) ? 0 : value;
    }
    const parsed = parseFloat(String(value ?? '0').replace(/,/g, ''));
    return isNaN(parsed) ? 0 : parsed;
  }
}
